import React from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LayoutDashboard, Users, CheckSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthStore } from '@/app/store/authStore';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
}

interface ClientManagerSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onNavClick?: () => void;
  isMobile?: boolean;
}

const ClientManagerSidebar: React.FC<ClientManagerSidebarProps> = ({
  isCollapsed,
  onToggle,
  onNavClick,
  isMobile,
}) => {
  const user = useAuthStore((state) => state.user);

  // Simple navigation items for client managers
  const navItems: NavItem[] = [
    { to: '/client-manager/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/client-manager/parties', icon: Users, label: 'My Parties' },
    { to: '/client-manager/tasks', icon: CheckSquare, label: 'Tasks' },
  ];

  // Handle nav click (for mobile close sidebar)
  const handleNavClick = () => {
    if (onNavClick) {
      onNavClick();
    }
  };

  // Handle navigation with support for Ctrl/Cmd+Click to open in new window (Electron multi-tab)
  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    // If Ctrl (Windows/Linux) or Cmd (Mac) key is pressed, open in new window (Electron)
    if ((e.ctrlKey || e.metaKey) && window.api?.window?.openNew) {
      e.preventDefault();
      window.api.window.openNew({ url: to });
      return;
    }
    // Otherwise, let React Router handle the navigation
    handleNavClick();
  };

  // Render a single nav item
  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;

    const navLink = (
      <NavLink
        to={item.to}
        onClick={(e) => handleNavLinkClick(e, item.to)}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
            'hover:bg-accent hover:text-accent-foreground',
            isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
            isCollapsed && 'justify-center px-2'
          )
        }
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!isCollapsed && <span>{item.label}</span>}
      </NavLink>
    );

    // If collapsed, wrap in tooltip
    if (isCollapsed) {
      return (
        <TooltipProvider key={item.to}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{navLink}</TooltipTrigger>
            <TooltipContent side="right">
              <p>{item.label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={item.to}>{navLink}</div>;
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col border-r bg-background transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-64',
        isMobile && 'w-64'
      )}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b px-4">
        {!isCollapsed && (
          <div className="flex flex-col">
            <h2 className="text-lg font-semibold">Task Manager</h2>
            {user?.role?.name && (
              <span className="text-xs text-muted-foreground capitalize">
                {user.role.name.replace('_', ' ')}
              </span>
            )}
          </div>
        )}
        {!isMobile && (
          <Button variant="ghost" size="icon" onClick={onToggle} className={cn(isCollapsed && 'mx-auto')}>
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="flex flex-col gap-1">{navItems.map((item) => renderNavItem(item))}</nav>
      </ScrollArea>

      {/* Footer - User info */}
      {!isCollapsed && user && (
        <div className="border-t p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <span className="text-sm font-medium">
                {user.full_name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || user.username?.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col overflow-hidden">
              <p className="text-sm font-medium truncate">{user.full_name || user.username}</p>
              <p className="text-xs text-muted-foreground truncate">{user.organization?.name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagerSidebar;
